import { Component, ChangeDetectionStrategy, inject, OnInit } from '@angular/core';

import { Observable, switchMap, take } from 'rxjs';
import { DIALOG_DATA, DialogRef, FileModel, Nullable, OkDialog } from '@pinup-teams/common';

import { SupportFormComponent } from './support-form.component';
import {
  FinancialRequestStatus,
  GetCoursesReq,
  GetFinancialCategoriesRes,
  GetLocationsRes,
  SupportCategories,
  SupportRequest,
} from '../../models';
import { environment } from '../../environments';
import { SUPPORT_FORM_API, SupportFormMockService, SupportFormService } from './api';

@Component({
  selector: 'pu-support-form-wrapper',
  standalone: true,
  imports: [SupportFormComponent],
  providers: [
    {
      provide: SUPPORT_FORM_API,
      useClass: environment.useMocks ? SupportFormMockService : SupportFormService,
    },
  ],
  template: `<pu-support-form
    [category]="category"
    [isLoading]="isLoading()"
    [courses]="courses"
    [locations]="locations"
    [financialCategories]="financialCategories"
    [requestIdHash]="requestIdHash"
    (closeDialog)="onSubmit($event)" />`,
  styles: `
    :host {
      display: flex;
      width: 720px;
      height: 100%;
      align-items: center;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportFormWrapperComponent implements OnInit {
  readonly #okDialog = inject(OkDialog);
  readonly #dialogRef = inject(DialogRef);
  readonly #supportFormService = inject(SUPPORT_FORM_API);
  readonly #data = inject<{ category: SupportCategories }>(DIALOG_DATA);

  readonly category = this.#data.category;
  readonly isLoading = this.#supportFormService.isLoading;

  requestIdHash: string;
  courses: Nullable<Observable<GetCoursesReq>> = null;
  locations: Nullable<Observable<GetLocationsRes>> = null;
  financialCategories: Nullable<Observable<GetFinancialCategoriesRes>> = null;

  ngOnInit(): void {
    switch (this.category) {
      case SupportCategories.education:
        this.courses = this.#supportFormService.getCourses();
        break;
      case SupportCategories.office:
        this.locations = this.#supportFormService.getLocations();
        break;
      case SupportCategories.finance:
        this.financialCategories = this.#supportFormService.getFinancialCategories();
        this.#supportFormService.getFinancialRequestIdHash().subscribe(response => (this.requestIdHash = response.request.requestIdHash));
        break;
    }
  }

  onSubmit(payload: SupportRequest): void {
    if (payload) {
      if (this.category === SupportCategories.finance) {
        this.#supportFormService
          .createFinancialRequest({
            requestType: 'message',
            categoryId: payload.financialCategory,
            requestIdHash: this.requestIdHash,
          })
          .pipe(
            take(1),
            switchMap(() =>
              this.#supportFormService.saveRequestEncryptedData({
                requestIdHash: this.requestIdHash,
                data: {
                  status: FinancialRequestStatus.New,
                  message: payload.text,
                },
                files: payload.images.map((file: FileModel) => {
                  const fileName = file.url.split('/')[5];

                  return fileName.slice(0, fileName.indexOf('?'));
                }),
              }),
            ),
          )
          .subscribe(() => this.#showThanksDialog());
      } else {
        this.#supportFormService.sendRequest(payload).subscribe(() => this.#showThanksDialog());
      }
    } else {
      this.#dialogRef.close();
    }
  }

  #showThanksDialog(): void {
    this.#dialogRef.close();

    this.#okDialog.open({
      headerKey: 'supportForm.contactOkDialog.header',
      messageKey: 'supportForm.contactOkDialog.message',
      subMessageKey: 'supportForm.contactOkDialog.subMessage',
      btnOkKey: 'supportForm.contactOkDialog.button.close',
      iconName: `${environment.shortName}-ai-chat-bot`,
      noCancel: true,
    });
  }
}
